import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import HBMediaElement from '../HBMediaElement/HBMediaElement';
import { vars } from '../../theme';
import HBIconButton, { IIconButtonProps } from '../HBIconButton/HBIconButton';
import HBClickable from '../HBClickable/HBClickable';
import HBFilePreview from '../HBFilePreview/HBFilePreview';
import { isCloudinaryImage } from '../common/image.utils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { DotsVertical16, Download16, Trash16 } from '@honeybook/icons';
import { IImage } from '../../types/image';
import HBMenu from '../HBMenu/HBMenu';
import HBText from '../HBText/HBText';

export interface Props {
    /** Marks this thumbnail as the default image; consumers use this to show a visual indicator. */
    isDefault?: boolean;
    /** Called when the user sets this image as the default; receives the image data object. */
    onSetAsDefault?: (image: IImage) => void;
    /** Called when the thumbnail is clicked; when provided, clicking selects the image instead of opening the preview. */
    onSelect?: (image: IImage) => void;
    /** Called when the user triggers a download action; receives the image data object. */
    onDownload: (image: IImage) => void;
    /** Called when the user triggers a delete action; receives the image data object. */
    onDelete?: (image: IImage) => void;
    /** Image data object containing url, name, cloudinary_public_id, and optional dataUrl for upload previews. */
    image: IImage;
    /** Action items rendered in the hover menu (top-right corner); each action maps to an HBMenu item with icon, label, and click handler. */
    actions: TImageThumbnailAction[];
    /** Defers image loading until the thumbnail enters the viewport. */
    lazyLoad?: boolean;
    /** Alt text for the image, passed to the underlying img element for accessibility. */
    alt?: string;
    /** Secondary info block shown at the bottom of the thumbnail on hover, with a title and content line. */
    additionalInfo?: {
        title: string;
        content: string;
    };
}

const HBImageThumbnail = ({
    image,
    onDownload,
    onDelete,
    onSelect,
    actions,
    lazyLoad = false,
    alt,
    additionalInfo
}: Props) => {
    const { isMediumUp } = useWindowSize();
    const [isPreviewShown, setIsPreviewShown] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleClick = () => {
        if (onSelect) {
            onSelect(image);
        } else {
            setIsPreviewShown(true);
        }
    };

    return (
        <Container
            onClick={handleClick}
            testIdName="image-thumbnail"
            shouldShowExpandedView={isMenuOpen}
        >
            <ImageComponent image={image} lazyLoad={lazyLoad} alt={alt} />
            {isMediumUp && (
                <ActionsContainer>
                    <MenuContainer
                        targetAsChild
                        preventClickPropogation
                        testIdName="actions"
                        menuItems={actions.map(action => ({
                            type: 'item' as const,
                            icon: <action.icon />,
                            title: action.label,
                            disabled: action.disabled,
                            onClick: action.onClick,
                            testIdName: action.testIdName,
                            theme: action.theme,
                            ...(action.tooltip && {
                                tooltip: {
                                    content: action.tooltip,
                                    ...action.tooltipProps
                                }
                            })
                        }))}
                        target={
                            <HBIconButton
                                testIdName="actions-menu"
                                icon={DotsVertical16}
                                label="Actions"
                            />
                        }
                        onOpenChange={(isOpen: boolean) =>
                            setIsMenuOpen(isOpen)
                        }
                    />
                </ActionsContainer>
            )}
            {isMediumUp && (
                <InfoContainer>
                    <HBText
                        type="emphasis14"
                        color="text.invertedPrimary"
                        ellipsis
                    >
                        {image.name}
                    </HBText>
                    {additionalInfo && (
                        <AdditionalInfoContainer>
                            <HBText
                                type="emphasis12"
                                color="text.invertedPrimary"
                                ellipsis
                            >
                                {additionalInfo?.title}
                            </HBText>
                            <HBText
                                type="emphasis12"
                                color="text.invertedPrimary"
                                ellipsis
                            >
                                {additionalInfo?.content}
                            </HBText>
                        </AdditionalInfoContainer>
                    )}
                </InfoContainer>
            )}
            {isPreviewShown && (
                <HBFilePreview
                    fileName={image.name}
                    fileUrl={image.url}
                    cloudinaryPublicId={image.cloudinary_public_id}
                    fileType="image"
                    onClose={() => setIsPreviewShown(false)}
                    onDownload={() => onDownload(image)}
                    onDelete={() => onDelete(image)}
                />
            )}
        </Container>
    );
};

export type TImageThumbnailAction = Pick<
    IIconButtonProps,
    'icon' | 'label' | 'disabled' | 'tooltip' | 'tooltipProps'
> & {
    testIdName: string;
    onClick: () => void;
    theme?: 'default' | 'destructive';
};

export const downloadAction: TImageThumbnailAction = {
    icon: Download16,
    label: 'Download',
    testIdName: 'download',
    onClick: () => {},
    theme: 'default'
};

export const deleteAction: TImageThumbnailAction = {
    icon: Trash16,
    label: 'Delete',
    testIdName: 'delete',
    onClick: () => {},
    theme: 'destructive'
};

const ImageComponent = ({
    image,
    lazyLoad,
    alt
}: {
    image: IImage;
    lazyLoad: boolean;
    alt?: string;
}) => {
    if (image.cloudinary_public_id || isCloudinaryImage(image.url)) {
        return (
            <CloudinaryImage
                path={image.cloudinary_public_id || image.url}
                height="120"
                crop="fill"
                lazyLoad={lazyLoad}
                alt={alt}
            />
        );
    } else if (image.url) {
        return <S3Image src={image.url} alt={image.name} />;
    } else if (image.dataUrl) {
        return <ImageDuringUpload src={image.dataUrl} alt={image.name} />;
    }
};

export default HBImageThumbnail;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    opacity: 0;
    background: linear-gradient(
        0deg,
        rgba(19, 20, 22, 0.53) 0%,
        rgba(58, 61, 67, 0.56) 25.1%,
        rgba(107, 113, 124, 0) 96.08%
    );
    padding: 5px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    box-sizing: border-box;
    transition: opacity 0.2s ease;
`;

const AdditionalInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const ActionsContainer = styled.div`
    opacity: 0;
    display: flex;
    position: absolute;
    top: 8px;
    right: 8px;
    width: 30px;
    height: 30px;
    border-radius: ${vars.radius.xs};
    background: ${vars.colors.surface.item};
    box-shadow: ${vars.shadow['10']};
    transition: opacity 0.2s ease;
`;

const MenuContainer = styled(HBMenu)`
    &:hover ${InfoContainer} {
        opacity: 1;
    }
`;

const Container = styled(HBClickable)<{ shouldShowExpandedView: boolean }>`
    height: 120px;
    position: relative;
    display: inline-block;

    ${({ shouldShowExpandedView }) =>
        shouldShowExpandedView &&
        css`
            ${ActionsContainer}, ${InfoContainer} {
                opacity: 1;
            }
        `}

    &:hover :is(${ActionsContainer}, ${InfoContainer}) {
        opacity: 1;
    }
`;

const CloudinaryImage = styled(HBMediaElement)`
    min-width: 100px;
    text-align: center;
`;

const S3Image = styled.img`
    height: 120px;
    min-width: 100px;
    object-fit: contain;
`;

const ImageDuringUpload = styled(S3Image)`
    opacity: 0.4;
`;
